import React, { useState } from "react"
import {
  Card,
  CardBody,
  Row,
  Col,
  Alert,
  Badge,
  Button,
  Input,
  Label,
  FormGroup,
} from "reactstrap"

// Niveles de aprobación según monto
const NIVELES_APROBACION = [
  {
    nivel: 1,
    nombre: "Analista de Crédito",
    montoHasta: 5000000,
    color: "info",
  },
  {
    nivel: 2,
    nombre: "Jefe de Créditos",
    montoHasta: 20000000,
    color: "primary",
  },
  {
    nivel: 3,
    nombre: "Comité de Crédito",
    montoHasta: 100000000,
    color: "warning",
  },
  { nivel: 4, nombre: "Directorio", montoHasta: Infinity, color: "danger" },
]

const ESTADOS_APROBACION = {
  pendiente: { label: "Pendiente", color: "secondary", icon: "bx-time-five" },
  en_revision: { label: "En Revisión", color: "info", icon: "bx-search" },
  aprobado: { label: "Aprobado", color: "success", icon: "bx-check-circle" },
  rechazado: { label: "Rechazado", color: "danger", icon: "bx-x-circle" },
  condicionado: {
    label: "Aprobado Condicionado",
    color: "warning",
    icon: "bx-error",
  },
}

// Timeline de aprobación
const TimelineAprobacion = ({ niveles, aprobaciones, nivelRequerido }) => {
  return (
    <div className="position-relative">
      {niveles
        .filter(n => n.nivel <= nivelRequerido)
        .map((nivel, idx) => {
          const aprobacion = aprobaciones[nivel.nivel]
          const estado = aprobacion?.estado || "pendiente"
          const estadoConfig = ESTADOS_APROBACION[estado]
          const esUltimo = idx === nivelRequerido - 1

          return (
            <div key={nivel.nivel} className="d-flex mb-4">
              {/* Línea vertical */}
              <div className="d-flex flex-column align-items-center me-3">
                <div
                  className={`rounded-circle d-flex align-items-center justify-content-center bg-${estadoConfig.color}`}
                  style={{ width: 40, height: 40 }}
                >
                  <i
                    className={`bx ${estadoConfig.icon} text-white`}
                    style={{ fontSize: 20 }}
                  />
                </div>
                {!esUltimo && (
                  <div
                    className={`flex-grow-1 bg-${estado === "aprobado" ? "success" : "secondary"}`}
                    style={{ width: 2, minHeight: 40 }}
                  />
                )}
              </div>

              {/* Contenido */}
              <div className="flex-grow-1">
                <Card className={`border-${estadoConfig.color} border`}>
                  <CardBody className="py-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{nivel.nombre}</strong>
                        <Badge color={estadoConfig.color} className="ms-2">
                          {estadoConfig.label}
                        </Badge>
                      </div>
                      <small className="text-muted">Nivel {nivel.nivel}</small>
                    </div>

                    {aprobacion && (
                      <div className="mt-2 pt-2 border-top">
                        {aprobacion.aprobador && (
                          <small className="d-block">
                            <i className="bx bx-user me-1" />
                            {aprobacion.aprobador}
                          </small>
                        )}
                        {aprobacion.fecha && (
                          <small className="d-block text-muted">
                            <i className="bx bx-calendar me-1" />
                            {aprobacion.fecha}
                          </small>
                        )}
                        {aprobacion.comentario && (
                          <small className="d-block mt-1 fst-italic">
                            {aprobacion.comentario}
                          </small>
                        )}
                      </div>
                    )}
                  </CardBody>
                </Card>
              </div>
            </div>
          )
        })}
    </div>
  )
}

// Resumen del dictamen
const ResumenDictamen = ({ datos }) => {
  const {
    cliente,
    montoSolicitado,
    plazo,
    tasa,
    nivelRiesgo,
    scoreKYC,
    cobertura,
  } = datos

  return (
    <Card className="mb-4">
      <CardBody>
        <h6 className="text-muted mb-3">RESUMEN DEL DICTAMEN CREDITICIO</h6>
        <Row>
          <Col md={6}>
            <table className="table table-sm table-borderless mb-0">
              <tbody>
                <tr>
                  <td className="text-muted">Cliente:</td>
                  <td className="fw-bold">{cliente || "-"}</td>
                </tr>
                <tr>
                  <td className="text-muted">Monto Solicitado:</td>
                  <td className="fw-bold">
                    ${(montoSolicitado || 0).toLocaleString("es-AR")}
                  </td>
                </tr>
                <tr>
                  <td className="text-muted">Plazo:</td>
                  <td>{plazo || "-"} meses</td>
                </tr>
                <tr>
                  <td className="text-muted">Tasa:</td>
                  <td>{tasa || "-"}%</td>
                </tr>
              </tbody>
            </table>
          </Col>
          <Col md={6}>
            <table className="table table-sm table-borderless mb-0">
              <tbody>
                <tr>
                  <td className="text-muted">Nivel de Riesgo:</td>
                  <td>
                    <Badge
                      color={
                        nivelRiesgo === "BAJO"
                          ? "success"
                          : nivelRiesgo === "MEDIO"
                            ? "warning"
                            : "danger"
                      }
                    >
                      {nivelRiesgo || "-"}
                    </Badge>
                  </td>
                </tr>
                <tr>
                  <td className="text-muted">Score KYC:</td>
                  <td>{scoreKYC || "-"}/100</td>
                </tr>
                <tr>
                  <td className="text-muted">Cobertura Garantías:</td>
                  <td
                    className={
                      cobertura >= 100 ? "text-success" : "text-warning"
                    }
                  >
                    {cobertura || 0}%
                  </td>
                </tr>
              </tbody>
            </table>
          </Col>
        </Row>
      </CardBody>
    </Card>
  )
}

// Formulario de decisión (para el nivel actual)
const FormDecision = ({ onDecision, nivelActual }) => {
  const [decision, setDecision] = useState("")
  const [comentario, setComentario] = useState("")
  const [condiciones, setCondiciones] = useState("")

  const handleSubmit = () => {
    onDecision({
      estado: decision,
      comentario,
      condiciones: decision === "condicionado" ? condiciones : null,
      fecha: new Date().toLocaleDateString("es-AR"),
      aprobador: "Usuario Actual", // En producción, obtener del contexto de auth
    })
  }

  return (
    <Card className="border-primary">
      <CardBody>
        <h6 className="mb-3">Registrar Decisión - {nivelActual}</h6>

        <FormGroup>
          <Label>Decisión *</Label>
          <div className="d-flex gap-2">
            <Button
              color={decision === "aprobado" ? "success" : "outline-success"}
              onClick={() => setDecision("aprobado")}
            >
              <i className="bx bx-check me-1" />
              Aprobar
            </Button>
            <Button
              color={
                decision === "condicionado" ? "warning" : "outline-warning"
              }
              onClick={() => setDecision("condicionado")}
            >
              <i className="bx bx-error me-1" />
              Aprobar Condicionado
            </Button>
            <Button
              color={decision === "rechazado" ? "danger" : "outline-danger"}
              onClick={() => setDecision("rechazado")}
            >
              <i className="bx bx-x me-1" />
              Rechazar
            </Button>
          </div>
        </FormGroup>

        {decision === "condicionado" && (
          <FormGroup>
            <Label>Condiciones de Aprobación *</Label>
            <Input
              type="textarea"
              rows={2}
              placeholder="Detalle las condiciones que debe cumplir..."
              value={condiciones}
              onChange={e => setCondiciones(e.target.value)}
            />
          </FormGroup>
        )}

        <FormGroup>
          <Label>Comentarios / Fundamentación</Label>
          <Input
            type="textarea"
            rows={3}
            placeholder="Ingrese sus comentarios..."
            value={comentario}
            onChange={e => setComentario(e.target.value)}
          />
        </FormGroup>

        <div className="d-flex justify-content-end">
          <Button
            color="primary"
            onClick={handleSubmit}
            disabled={
              !decision || (decision === "condicionado" && !condiciones)
            }
          >
            Confirmar Decisión
          </Button>
        </div>
      </CardBody>
    </Card>
  )
}

// Componente principal
const Aprobacion = ({
  datosSolicitud = {},
  aprobaciones = {},
  setAprobaciones,
  onAprobacionFinal,
}) => {
  // Determinar nivel requerido según monto
  const monto = datosSolicitud.montoSolicitado || 0
  const nivelRequerido =
    NIVELES_APROBACION.find(n => monto <= n.montoHasta)?.nivel || 4

  // Determinar nivel actual (próximo a aprobar)
  const nivelActual = Object.keys(aprobaciones).length + 1

  // Verificar si está completamente aprobado o rechazado
  const ultimaAprobacion = aprobaciones[Object.keys(aprobaciones).length]
  const estaAprobado =
    nivelActual > nivelRequerido && ultimaAprobacion?.estado === "aprobado"
  const estaRechazado = ultimaAprobacion?.estado === "rechazado"
  const estaCondicionado =
    ultimaAprobacion?.estado === "condicionado" && nivelActual > nivelRequerido

  const handleDecision = decision => {
    setAprobaciones(prev => ({
      ...prev,
      [nivelActual]: decision,
    }))

    // Si es el último nivel y aprobó, notificar
    if (
      nivelActual >= nivelRequerido &&
      decision.estado === "aprobado" &&
      onAprobacionFinal
    ) {
      onAprobacionFinal(decision)
    }
  }

  return (
    <div>
      <h5 className="mb-3">Circuito de Aprobación</h5>
      <p className="text-muted mb-4">
        Esta solicitud requiere aprobación hasta el Nivel {nivelRequerido} (
        {NIVELES_APROBACION.find(n => n.nivel === nivelRequerido)?.nombre}).
      </p>

      {/* Resumen */}
      <ResumenDictamen datos={datosSolicitud} />

      {/* Estado final */}
      {estaAprobado && (
        <Alert color="success" className="mb-4">
          <i className="bx bx-check-circle me-2" style={{ fontSize: 24 }} />
          <strong>SOLICITUD APROBADA</strong>
          <p className="mb-0 mt-1">
            La solicitud ha sido aprobada por todos los niveles requeridos.
          </p>
        </Alert>
      )}

      {estaRechazado && (
        <Alert color="danger" className="mb-4">
          <i className="bx bx-x-circle me-2" style={{ fontSize: 24 }} />
          <strong>SOLICITUD RECHAZADA</strong>
          <p className="mb-0 mt-1">
            {ultimaAprobacion?.comentario || "La solicitud ha sido rechazada."}
          </p>
        </Alert>
      )}

      {estaCondicionado && (
        <Alert color="warning" className="mb-4">
          <i className="bx bx-error me-2" style={{ fontSize: 24 }} />
          <strong>APROBACIÓN CONDICIONADA</strong>
          <p className="mb-0 mt-1">{ultimaAprobacion?.condiciones}</p>
        </Alert>
      )}

      {/* Timeline */}
      <Row>
        <Col md={7}>
          <TimelineAprobacion
            niveles={NIVELES_APROBACION}
            aprobaciones={aprobaciones}
            nivelRequerido={nivelRequerido}
          />
        </Col>
        <Col md={5}>
          {/* Formulario si está pendiente */}
          {!estaAprobado &&
            !estaRechazado &&
            !estaCondicionado &&
            nivelActual <= nivelRequerido && (
              <FormDecision
                onDecision={handleDecision}
                nivelActual={
                  NIVELES_APROBACION.find(n => n.nivel === nivelActual)?.nombre
                }
              />
            )}
        </Col>
      </Row>
    </div>
  )
}

export default Aprobacion
